import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { ConfirmDialog } from "./Confirm";
import { pageQuery, withWindowsOf } from "./ToolWindows";

/*
 * Asks before leaving with unsaved changes (while `when`):
 *  - links in the app and the browser's Back/Forward: our own dialog (the browser's confirm()
 *    can be blocked inside ServiceM8's frame), then goes on if you choose to leave;
 *  - reloading, closing the tab or going to another site: the browser's own warning.
 * `window`: a record window's (its address key and value, e.g. ["item", uuid]): it stays open as you move between pages (links
 * carry it: ToolWindows.tsx), so only going somewhere without it (Back to before it opened, a link that closes it) asks.
 * Links inside a window carry the open windows (RecordWindow.tsx), so they're judged by where they really go.
 */

const here = () => location.pathname + location.search + location.hash;

export function LeaveGuard({ when, message, window }: Readonly<{ when: boolean; message: string; window?: readonly [key: string, value: string] }>) {
    const [, navigate] = useLocation();
    // What leaving does, while the dialog asks (null: not asking).
    const [pending, setPending] = useState<(() => void) | null>(null);
    useEffect(() => {
        if (!when) return;
        const page = here();
        // "~": the full address, not one under the router the page is in (e.g. /c/:companyId).
        const askToGo = (to: string) => setPending(() => () => navigate(`~${to}`));
        const onBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault();
        const [key, value] = window ?? [];
        // Where it goes leaves nothing: the same page (whatever windows open or close), or this window's still open there.
        const keeps = (url: URL) =>
            key ? url.searchParams.getAll(key)[0] === value : url.pathname + pageQuery(url.search) === location.pathname + pageQuery(location.search);
        // Caught before the app's own handlers (capture, on document), so the link doesn't navigate.
        const onClick = (e: MouseEvent) => {
            if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            const link = (e.target as Element | null)?.closest?.("a[href]");
            if (!(link instanceof HTMLAnchorElement) || (link.target && link.target !== "_self") || link.hasAttribute("download")) return;
            let url = new URL(link.href, location.href);
            // Another site: the browser's warning covers it.
            if (url.origin !== location.origin) return;
            if (link.closest(".record-window")) url = new URL(withWindowsOf(location.search)(url.pathname + url.search + url.hash), location.href);
            if (keeps(url)) return;
            e.preventDefault();
            e.stopPropagation();
            askToGo(url.pathname + url.search + url.hash);
        };
        // Back/Forward: put this page's address back before the app sees the change (a capture
        // listener on window runs first), and ask.
        const onPopState = (e: PopStateEvent) => {
            const target = here();
            if (target === page || keeps(new URL(target, location.href))) return;
            e.stopImmediatePropagation();
            history.pushState(history.state, "", page);
            askToGo(target);
        };
        addEventListener("beforeunload", onBeforeUnload);
        document.addEventListener("click", onClick, true);
        addEventListener("popstate", onPopState, true);
        return () => {
            removeEventListener("beforeunload", onBeforeUnload);
            document.removeEventListener("click", onClick, true);
            removeEventListener("popstate", onPopState, true);
        };
    }, [when, navigate, window?.[0], window?.[1]]);

    const leave = () => {
        const go = pending;
        setPending(null);
        go?.();
    };

    // The same dialog as every confirmation (Confirm.tsx); staying is the safe choice, so it has the focus.
    return (
        <ConfirmDialog
            open={pending !== null}
            title="Leave without saving?"
            message={message}
            confirmLabel="Leave without saving"
            cancelLabel="Stay"
            tone="danger"
            onConfirm={leave}
            onCancel={() => setPending(null)}
        />
    );
}

